//
//
// countup.js
//
// Initializes the countup.js library with IntersectionObserver for scroll-triggered animations
// Countup starts when elements with [data-countup] scroll into view
import { CountUp } from "countup.js"

/**
 * ------------------------------------------------------------------------
 * Constants
 * ------------------------------------------------------------------------
 */

const VERSION = '2.0.0';
const DATA_KEY = 'mr.countup';

const Selector = {
  DATA_COUNTUP: '[data-countup]',
};

/**
 * ------------------------------------------------------------------------
 * Helper Functions
 * ------------------------------------------------------------------------
 */

// Helper to get dataset value and convert to appropriate type
const getDataAttr = (element, key, defaultValue = null) => {
  const value = element.dataset[key];
  if (value === undefined || value === '') return defaultValue;

  // Handle boolean values
  if (value === 'true') return true;
  if (value === 'false') return false;

  return value;
};

/**
 * ------------------------------------------------------------------------
 * Class Definition
 * ------------------------------------------------------------------------
 */

class Countup {
  constructor(element) {
    // Grab data-attributes
    // Note: dataset converts kebab-case to camelCase (data-decimal-places -> dataset.decimalPlaces)
    this.start = parseFloat(getDataAttr(element, 'start', '0'), 10);
    // For 'end', if data-end is not set or empty, use element text content
    const endValue = getDataAttr(element, 'end');
    this.end = parseFloat((endValue && endValue !== '') ? endValue : element.textContent, 10) || 0;
    this.duration = parseFloat(getDataAttr(element, 'duration', '2.5'), 10);
    this.grouping = getDataAttr(element, 'grouping', 'false') === 'true';
    this.separator = getDataAttr(element, 'separator', ',');
    this.decimalCharacter = getDataAttr(element, 'decimalCharacter', '.');
    this.decimalPlaces = parseInt(getDataAttr(element, 'decimalPlaces', '0'), 10);
    this.prefix = getDataAttr(element, 'prefix', '');
    this.suffix = getDataAttr(element, 'suffix', '');
    // the easing data attribute will only disable easing if false is specified. Defaults to true.
    const easing = getDataAttr(element, 'easing');
    this.easing = easing === false ? false : true;
    this.element = element;
    this.hasAnimated = false;

    this.initCountUp();
    this.initObserver();
  }

  // getters

  static get VERSION() {
    return VERSION;
  }

  initCountUp() {
    this.countUpInstance = new CountUp(
      this.element,
      this.end,
      {
        startVal: this.start,
        decimalPlaces: this.decimalPlaces,
        duration: this.duration,
        useEasing: this.easing,
        useGrouping: this.grouping,
        separator: this.separator,
        decimal: this.decimalCharacter,
        prefix: this.prefix,
        suffix: this.suffix,
      },
    );

    if (this.countUpInstance.error) {
      console.error('CountUp error:', this.countUpInstance.error);
    }
  }

  initObserver() {
    // Use IntersectionObserver to detect when element enters viewport
    const options = {
      root: null, // viewport
      rootMargin: '0px',
      threshold: 0.5 // trigger when 50% of element is visible
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.hasAnimated) {
          this.startCounting();
          this.hasAnimated = true;
          // Optionally stop observing after first animation
          this.observer.unobserve(this.element);
        }
      });
    }, options);

    this.observer.observe(this.element);
  }

  startCounting() {
    if (!this.countUpInstance.error) {
      this.countUpInstance.start();
    }
  }
}

/**
 * ------------------------------------------------------------------------
 * Initialise by data attribute
 * ------------------------------------------------------------------------
 */

const initCountup = () => {
  const countupElements = document.querySelectorAll(Selector.DATA_COUNTUP);

  countupElements.forEach((element) => {
    // Store instance on element to prevent re-initialization
    if (!element[DATA_KEY]) {
      element[DATA_KEY] = new Countup(element);
    }
  });
};

// Auto-initialize on turbo:load for subsequent navigations
document.addEventListener('turbo:load', initCountup);

// Also initialize immediately in case script loads after turbo:load has fired (deferred scripts)
initCountup();

export default Countup;
